import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function OwnerPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // plan_id -> {price_gbp,label,subtitle,duration_days}
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans);
        const seed = {};
        data.plans.forEach((p) => {
          seed[p.plan_id] = {
            price_gbp: p.price_gbp,
            label: p.label,
            subtitle: p.subtitle || "",
            duration_days: p.duration_days,
          };
        });
        setEdits(seed);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateField = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const save = async (planId) => {
    setSavingId(planId);
    try {
      const e = edits[planId];
      const payload = {
        price_gbp: Number(e.price_gbp),
        label: e.label,
        subtitle: e.subtitle,
        duration_days: Number(e.duration_days),
      };
      const { data } = await api.put(`/admin/plans/${planId}`, payload);
      setPlans((prev) => prev.map((p) => (p.plan_id === planId ? data : p)));
      toast.success(`${data.label} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="text-[#5C6B61]">Loading…</div>;

  return (
    <section className="space-y-5" data-testid="owner-plans-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="ff-label">Subscription pricing</div>
          <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Shop, Cal & Meal Pro plans
          </h2>
          <p className="text-sm text-[#5C6B61] mt-1">
            Prices show on the public pricing page immediately. Currency: GBP.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.plan_id} className="ff-card p-5" data-testid={`owner-plan-${p.plan_id}`}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{p.label}</h3>
              <span className="text-[10px] uppercase tracking-wider text-[#8B9690] font-medium">{p.plan_id}</span>
            </div>
            <div className="mt-4 space-y-3">
              <Field label="Price (£)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={edits[p.plan_id]?.price_gbp ?? ""}
                  onChange={(e) => updateField(p.plan_id, "price_gbp", e.target.value)}
                  data-testid={`owner-plan-price-${p.plan_id}`}
                  className="ff-input text-sm w-full"
                />
              </Field>
              <Field label="Label">
                <input
                  type="text"
                  value={edits[p.plan_id]?.label ?? ""}
                  onChange={(e) => updateField(p.plan_id, "label", e.target.value)}
                  className="ff-input text-sm w-full"
                />
              </Field>
              <Field label="Subtitle">
                <input
                  type="text"
                  value={edits[p.plan_id]?.subtitle ?? ""}
                  onChange={(e) => updateField(p.plan_id, "subtitle", e.target.value)}
                  className="ff-input text-sm w-full"
                />
              </Field>
              <Field label="Duration (days)">
                <input
                  type="number"
                  min="1"
                  value={edits[p.plan_id]?.duration_days ?? ""}
                  onChange={(e) => updateField(p.plan_id, "duration_days", e.target.value)}
                  className="ff-input text-sm w-full"
                />
              </Field>
            </div>
            <button
              onClick={() => save(p.plan_id)}
              disabled={savingId === p.plan_id}
              data-testid={`owner-plan-save-${p.plan_id}`}
              className="w-full mt-4 ff-btn-primary text-sm disabled:opacity-60"
            >
              {savingId === p.plan_id ? "Saving…" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[#5C6B61] font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}
